import React from "react";
import { ArrowUpDown, ListFilter } from "lucide-react";

import { personName } from "@/shared/api/members";
import { GhostButton, RemovableChip, SearchInput } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { menuCls, menuLabelCls } from "./KbAddMenu";
import { FIRST_DESCENDING, NOBODY, NO_FILTERS, STATUS_LABEL, facets, filterCount, type FileStatus, type Filters, type Row, type Sort, type SortKey } from "./listing";

import type { PersonRef } from "@/shared/types";

const itemCls = "pl-8 pr-2 py-[5px] rounded-[8px] text-[13px] text-[var(--text-primary)] cursor-pointer focus:bg-[var(--accent-hover)]";

const SORT_LABEL: Record<SortKey, string> = { name: "Name", modified: "Modified", size: "Size", tokens: "Tokens" };
/** How each sort reads in each direction: [ascending, descending]. */
const ORDER_LABEL: Record<SortKey, [string, string]> = {
  name: ["A to Z", "Z to A"],
  modified: ["Oldest first", "Newest first"],
  size: ["Smallest first", "Largest first"],
  tokens: ["Fewest first", "Most first"],
};

type Group = keyof Filters;

export function ownerLabel(id: string, person: PersonRef | null | undefined, me?: string) {
  if (id === NOBODY || !person) return "Unknown";
  return id === me ? "You" : personName(person);
}

function SortMenu({ sort, onSort }: { sort: Sort; onSort: (sort: Sort) => void }) {
  const [up, down] = ORDER_LABEL[sort.key];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GhostButton aria-label={`Sort by ${SORT_LABEL[sort.key]}, ${sort.descending ? down : up}`} className="px-2.5 sm:px-3">
          <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="hidden sm:inline">{SORT_LABEL[sort.key]}</span>
        </GhostButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`w-[200px] ${menuCls}`}>
        <DropdownMenuLabel className={menuLabelCls}>Sort by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sort.key} onValueChange={(key) => onSort({ key: key as SortKey, descending: FIRST_DESCENDING[key as SortKey] })}>
          {(Object.keys(SORT_LABEL) as SortKey[]).map((key) => (
            <DropdownMenuRadioItem key={key} value={key} className={itemCls}>{SORT_LABEL[key]}</DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
        <DropdownMenuRadioGroup value={sort.descending ? "down" : "up"} onValueChange={(v) => onSort({ key: sort.key, descending: v === "down" })}>
          <DropdownMenuRadioItem value="up" className={itemCls}>{up}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="down" className={itemCls}>{down}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FilterMenu({ rows, filters, onFilters, people, me }: {
  rows: Row[];
  filters: Filters;
  onFilters: (f: Filters) => void;
  people: Map<string, PersonRef | null>;
  me?: string;
}) {
  const found = facets(rows, filters);
  const on = filterCount(filters);
  const toggle = (group: Group, value: string) => {
    const values = filters[group] as string[];
    onFilters({ ...filters, [group]: values.includes(value) ? values.filter((v) => v !== value) : [...values, value] });
  };
  const section = (group: Group, title: string, options: { value: string; count: number; label: string }[]) =>
    options.length > 0 && (
      <>
        <DropdownMenuLabel className={menuLabelCls}>{title}</DropdownMenuLabel>
        {options.map((o) => (
          <DropdownMenuCheckboxItem
            key={o.value}
            checked={(filters[group] as string[]).includes(o.value)}
            onCheckedChange={() => toggle(group, o.value)}
            onSelect={(e) => e.preventDefault()} // stay open to pick several
            className={itemCls}
          >
            <span className="flex-1 min-w-0 truncate">{o.label}</span>
            <span className="ml-3 font-mono text-[11px] tabular-nums text-[var(--text-quaternary)]">{o.count}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GhostButton aria-label={on ? `Filter, ${on} on` : "Filter"} className="px-2.5 sm:px-3">
          <ListFilter className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="hidden sm:inline">Filter</span>
          {on > 0 && (
            <span className="min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--brand)] text-[var(--brand-foreground)] text-[10px] font-semibold tabular-nums flex items-center justify-center">{on}</span>
          )}
        </GhostButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`w-[232px] max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] ${menuCls}`}>
        {section("types", "Type", found.types.map((f) => ({ ...f, label: f.value })))}
        {section("statuses", "Status", found.statuses.map((f) => ({ ...f, label: STATUS_LABEL[f.value as FileStatus] })))}
        {section("owners", "Owner", found.owners.map((f) => ({ ...f, label: ownerLabel(f.value, people.get(f.value), me) })))}
        {!rows.length && !on && <p className="m-0 px-2 py-3 text-[12.5px] text-[var(--text-tertiary)]">Nothing to filter yet.</p>}
        {on > 0 && (
          <>
            <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
            <DropdownMenuItem onSelect={() => onFilters(NO_FILTERS)} className="px-2 py-[5px] rounded-[8px] text-[13px] text-[var(--text-secondary)] cursor-pointer focus:bg-[var(--accent-hover)]">
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* Above the table: the search across the base, Filter and Sort, how many rows show, and
   (on a phone, where the rail steps aside) New. The filters that are on sit under it as
   chips that come off with their ×. */
export function KbToolbar({ base, query, onQuery, rows, shown, filters, onFilters, sort, onSort, people, me, aside }: {
  base: string;
  query: string;
  onQuery: (query: string) => void;
  /** The rows before the filters: what the filter menu offers and counts. */
  rows: Row[];
  shown: number;
  filters: Filters;
  onFilters: (filters: Filters) => void;
  sort: Sort;
  onSort: (sort: Sort) => void;
  /** Who each owner is, across the base. */
  people: Map<string, PersonRef | null>;
  me?: string;
  aside?: React.ReactNode;
}) {
  const searching = query.trim().length > 0;
  const total = rows.length;
  const count = searching ? `${shown} ${shown === 1 ? "result" : "results"}` : shown === total ? `${total} ${total === 1 ? "item" : "items"}` : `${shown} of ${total} items`;
  const chips: { group: Group; value: string; label: string }[] = [
    ...filters.types.map((value) => ({ group: "types" as const, value, label: value })),
    ...filters.statuses.map((value) => ({ group: "statuses" as const, value, label: STATUS_LABEL[value] })),
    ...filters.owners.map((value) => ({ group: "owners" as const, value, label: ownerLabel(value, people.get(value), me) })),
  ];
  const remove = (group: Group, value: string) => onFilters({ ...filters, [group]: (filters[group] as string[]).filter((v) => v !== value) });

  return (
    <div className="shrink-0 px-4 md:px-5 pt-4 pb-3 space-y-2.5">
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
        <SearchInput
          aria-label="Search this base"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && onQuery("")}
          placeholder={`Search ${base}`}
          className="w-full sm:w-auto sm:flex-1 min-w-0 sm:max-w-[360px] [&_input]:text-[16px] md:[&_input]:text-[13px]"
        />
        <FilterMenu rows={rows} filters={filters} onFilters={onFilters} people={people} me={me} />
        <SortMenu sort={sort} onSort={onSort} />
        <span className="flex-1" />
        <span aria-live="polite" className="hidden lg:block shrink-0 whitespace-nowrap text-[12.5px] tabular-nums text-[var(--text-tertiary)]">{count}</span>
        {aside}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <RemovableChip key={`${c.group}:${c.value}`} removeLabel={`Remove ${c.label}`} onRemove={() => remove(c.group, c.value)}>
              <span className="truncate">{c.label}</span>
            </RemovableChip>
          ))}
          <button type="button" onClick={() => onFilters(NO_FILTERS)} className="press h-7 px-2 rounded-[8px] text-[12px] text-[var(--text-tertiary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
